import { useContext, useEffect, useState } from "react";
import { Screen } from "../../../main/components/screen/Screen";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import { MainContext } from "../../../main/context/MainContextProvider";
import { usinasApi } from "../../../services/api";
import { ErrorModal } from "../../../main/components/ErrorModal";
import { NotFoundError } from "../../../errors";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import DatePickerLocalizationAdapter from "../../../main/context/DatePickerLocalizationAdapter";
import CardBox from "../../../main/components/CardBox";
import InverterPerformanceCard from "../components/report_cards/InverterPerformanceChart";
import UnitPerformanceChart from "../components/report_cards/UnitPerformanceChart";
import ParetoReportChart from "../components/report_cards/ParetoReportChart";
import ExpectedAndGeneratedValueChart from "../components/report_cards/ExpectedAndGeneratedValueChart";
import GenerationComparisonChart from "../components/report_cards/GenerationComparisonChart";
import LossDiagramReportChart from "../components/report_cards/LossDiagramReportChart";
import { useNavigate } from "react-router-dom";
import {
  formatDateAndHour,
  isNumberNullOrNegative,
  numberPtBr,
  numberPtBrPercent01,
  numberPtBrPercent0100,
} from "../../../main/utils/utils";
import ParetoTable from "../components/report_cards/ParetoTable";
import GeneratedEnergyCharts from "../components/report_cards/GeneratedEnergyCharts";
import { transformDateFormat } from "../../dashboard/helpers/format-number";
import PerformanceRatioChart from "../components/report_cards/PerformanceRatioChart";
import GeneratedAvailabilityCharts from "../components/report_cards/GeneratedAvailabilityChart";
import MTTRAndMTBFChart from "../../dashboard/components/MTTRAndMTBFChart";
import { Usina } from "../types";
import StringsHeatmap from "../components/report_cards/StringsHeatmap";
import PerformanceMonthlyChart from "../components/report_cards/PerformanceMonthly";

export default function ReportDaily() {
  const {
    selectedDashboardUsina: selectedUsina,
    toastNotifications,
    setToastNotifications,
    relatorioDiarioLoading,
    relatorioDiario,
    setRelatorioDiarioLoading,
    setRelatorioDiario,
    theme,
  } = useContext(MainContext);

  const primary = theme.muiTheme.palette.primary.main;

  const [isLoadingDailyReport, setIsLoadingDailyReport] =
    useState<boolean>(false);
  const [reportDate, setReportDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [reportErrors, setReportErrors] = useState<string[]>([]);

  const [errorModalData, setErrorModalData] = useState<{
    title: string;
    message: string;
  }>({ title: "", message: "" });

  const handleGetDailyReport = async () => {
    setIsLoadingDailyReport(true);
    try {
      const pdfResponse = await usinasApi.getDailyReportUsina(
        reportDate,
        selectedUsina?.usinaIds.id ?? ""
      );

      // Download by creating a link and clicking it to work on all browsers
      const downloadButton = document.createElement("a");
      downloadButton.href = pdfResponse.data.url;
      downloadButton.download = `relatorio-diario-${reportDate}.pdf`;
      downloadButton.click();
    } catch (error) {
      if (error instanceof NotFoundError) {
        setErrorModalData({
          title: "Relatório não encontrado",
          message: `O relatório do dia ${reportDate
            .split("-")
            .reverse()
            .join("/")
            .replace(/\/\//g, "/")} não está disponível.
            Tente gerar o relatório antes de buscar o pdf.`,
        });
        return;
      }
      setErrorModalData({
        title: "Erro ao buscar pdf",
        message: "Ocorreu um erro ao buscar o pdf, tente novamente.",
      });
    } finally {
      setIsLoadingDailyReport(false);
    }
  };

  const closeErrorModal = (open: boolean) => {
    !open && setErrorModalData({ title: "", message: "" });
  };
  const showErrorModal = errorModalData.title !== "";

  const today = dayjs(new Date());
  const dayjsDate = reportDate ? dayjs(reportDate) : null;

  const navigate = useNavigate();

  const getRelatorioDiario = async (reportDate: string) => {
    setRelatorioDiarioLoading(true);
    const newNotifications = {
      isAdding: true,
      notifications: [
        ...toastNotifications.notifications,
        {
          severity: "info" as const,
          text: "A busca foi inicializada, voce será notificado assim que ela for finalizada!",
        },
      ],
    };
    setToastNotifications(newNotifications);
    try {
      const reportDailyResponse = await usinasApi.getDailyReportInfoUsina(
        reportDate,
        selectedUsina?.usinaIds.id ?? ""
      );

      setRelatorioDiario(reportDailyResponse.data.relatorioDiario);
      setReportErrors(reportDailyResponse.data.errors);
      const newNotifications = {
        isAdding: true,
        notifications: [
          ...toastNotifications.notifications,
          {
            severity: "success" as const,
            text: (
              <>
                Relatório diário gerado com sucesso!{" "}
                <a
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/usinas/boletim-diario")}
                >
                  Clique aqui
                </a>{" "}
                para vizualizar.
              </>
            ),
          },
        ],
      };
      setToastNotifications(newNotifications);
    } catch (error) {
      const newNotifications = {
        isAdding: true,
        notifications: [
          ...toastNotifications.notifications,
          {
            severity: "error" as const,
            text: "Erro ao gerar relatório diário, tente novamente!",
          },
        ],
      };
      setToastNotifications(newNotifications);
    } finally {
      setRelatorioDiarioLoading(false);
    }
  };

  const [currentUsina, setCurrentUsina] = useState<Usina>();

  const getUsina = async (selectedUsinaId: string) => {
    try {
      if (!selectedUsinaId) return;
      const reportDailyResponse = await usinasApi.getUsina(selectedUsinaId);
      setCurrentUsina(reportDailyResponse);
    } catch {
      console.error(
        `Houve um erro inesperado ao tentar buscar os detalhes da usina ${selectedUsina?.name}`
      );
    }
  };

  useEffect(() => {
    getUsina(selectedUsina?.usinaIds.id ?? "");
  }, [selectedUsina]);

  const getExportButtonTooltipText = (
    usinaHasPermission: boolean | null | undefined,
    usinaId: string | undefined
  ): React.ReactNode => {
    if (!usinaHasPermission) {
      return (
        <>
          A usina não tem permissão para relatório diário,{" "}
          <Box
            sx={{ cursor: "pointer", color: primary }}
            onClick={() => navigate(`/usinas/${usinaId}`)}
          >
            Clique aqui para alterar a permissão da usina
          </Box>
        </>
      );
    }

    if (isLoadingDailyReport)
      return "Aguarde a exportação do relatório diário.";

    if (!reportDate) return "Selecione uma data para geração de relatório.";


    return "";
  };

  const getGenerateButtonTooltipText = (
    usinaHasPermission: boolean | null | undefined,
    usinaId: string
  ): React.ReactNode => {
    if (!usinaHasPermission) {
      return (
        <>
          A usina não tem permissão para relatório diário,{" "}
          <Box
            sx={{ cursor: "pointer", color: primary }}
            onClick={() => navigate(`/usinas/${usinaId}`)}
          >
            Clique aqui para alterar a permissão da usina
          </Box>
        </>
      );
    }

    if (relatorioDiarioLoading) return "Aguarde a busca do relatório diário.";

    if (!reportDate) return "Selecione uma data para geração de relatório.";

    return "";
  };

  const disponibilidadeOnOffEnabled =
    relatorioDiario?.compiladoUsina?.disponibilidadeOnOffEnabled;

  const disponibilidadeMedia = isNumberNullOrNegative(
    relatorioDiario?.disponibilidadeDiaria?.disponibilidadeMedia
  )
    ? "-"
    : `${numberPtBrPercent0100(
      relatorioDiario?.disponibilidadeDiaria?.disponibilidadeMedia ?? 0,
      1,
      0
    )}`;

  const disponibilidadeTotal =
    isNumberNullOrNegative(relatorioDiario?.disponibilidadeTotal?.value) ||
      (relatorioDiario?.disponibilidadeTotal?.value ?? 0) > 100
      ? "-"
      : `${numberPtBrPercent01(
        relatorioDiario?.disponibilidadeTotal?.value ?? 0,
        1,
        0
      )}`;

  const disponibilidadeOnOff = isNumberNullOrNegative(
    relatorioDiario?.compiladoUsina?.disponibilidadeOnOff
  )
    ? "-"
    : `${numberPtBrPercent01(
      relatorioDiario?.compiladoUsina?.disponibilidadeOnOff ?? 0,
      1,
      0
    )}`;

  const potenciaMedia = isNumberNullOrNegative(
    relatorioDiario?.potenciaAtivaMedia?.value
  )
    ? "-"
    : `${numberPtBr(relatorioDiario?.potenciaAtivaMedia?.value ?? 0, 2)} MW`;

  const prRealizado =
    isNumberNullOrNegative(
      relatorioDiario?.prIrradiacaoPrevistoRealizado?.prRealizado
    ) ||
      (relatorioDiario?.prIrradiacaoPrevistoRealizado?.prRealizado ?? 0) > 150
      ? "-"
      : `${numberPtBrPercent01(
        relatorioDiario?.prIrradiacaoPrevistoRealizado?.prRealizado ?? 0
      )}`;

  const irradiacaoMedia =
    isNumberNullOrNegative(
      relatorioDiario?.prIrradiacaoPrevistoRealizado?.irradiacaoRealizada
    ) ||
      (relatorioDiario?.prIrradiacaoPrevistoRealizado?.irradiacaoRealizada ?? 0) >
      200
      ? "-"
      : `${numberPtBrPercent0100(
        relatorioDiario?.prIrradiacaoPrevistoRealizado?.irradiacaoRealizada ??
        0
      )}`;

  const energiaGerada = isNumberNullOrNegative(
    relatorioDiario?.compiladoUsina?.energiaGerada
  )
    ? 0
    : parseFloat(numberPtBr(relatorioDiario?.compiladoUsina?.energiaGerada ?? 0));
  const energiaPrevista = isNumberNullOrNegative(
    relatorioDiario?.compiladoUsina?.energiaPrevista
  )
    ? 0
    : parseFloat(numberPtBr(relatorioDiario?.compiladoUsina?.energiaPrevista ?? 0));
  const energiaIdeal = isNumberNullOrNegative(
    relatorioDiario?.compiladoUsina?.energiaIdeal
  )
    ? 0
    : parseFloat(numberPtBr(relatorioDiario?.compiladoUsina?.energiaIdeal ?? 0));

  return (
    <Screen.Root
      sx={{
        padding: "20px",
        height: "100vh",
        display: "flex",
        overflow: "auto",
        alignContent: "center",
      }}
    >
      <Screen.Container removePadding sx={{ height: "100%" }}>
        <Grid
          container
          sx={{
            height: "100%",
            paddingBottom: "16px",
          }}
          spacing={2}
        >
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "20px",
              alignItems: "center",
              justifyContent: relatorioDiario ? undefined : "center",
            }}
          >
            <Box
              sx={{
                maxWidth: "400px",
                minWidth: { xs: "250px", md: "400px" },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Relatório Diário
              </Typography>
              <DatePickerLocalizationAdapter>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={dayjsDate}
                  onChange={(e) => e && setReportDate(e.format("YYYY-MM-DD"))}
                  maxDate={today}
                  label={"Data do Relatório"}
                  sx={{
                    width: "100%",
                    marginY: "12px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                    },
                  }}
                />
              </DatePickerLocalizationAdapter>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <Tooltip
                  title={getExportButtonTooltipText(
                    currentUsina?.reportOptions?.dailyReportEnabled,
                    currentUsina?.usinaIds.id
                  )}
                >
                  <Box
                    sx={{
                      width: { sx: "100%", md: "49%" },
                      marginBottom: { xs: "8px", md: "0px" },
                    }}
                  >
                    <PrimaryBaseButton
                      disabled={
                        !currentUsina?.reportOptions?.dailyReportEnabled ||
                        isLoadingDailyReport ||
                        !reportDate
                      }
                      sx={{ width: "100%" }}
                      loading={isLoadingDailyReport}
                      title="Exportar PDF"
                      onClick={handleGetDailyReport}
                    />
                  </Box>
                </Tooltip>
                <Tooltip
                  title={getGenerateButtonTooltipText(
                    currentUsina?.reportOptions?.dailyReportEnabled,
                    currentUsina?.usinaIds.id ?? ""
                  )}
                >
                  <Box sx={{ width: { sx: "100%", md: "49%" } }}>
                    <PrimaryBaseButton
                      disabled={
                        !currentUsina?.reportOptions?.dailyReportEnabled ||
                        !reportDate ||
                        relatorioDiarioLoading
                      }
                      sx={{ width: "100%" }}
                      loading={relatorioDiarioLoading}
                      title="Gerar Relatório"
                      onClick={() => getRelatorioDiario(reportDate)}
                    />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
          {relatorioDiario && (
            <>
              <Grid sx={{ width: "100%" }} item xs={12}>
                <Typography variant="h6" sx={{ paddingBottom: "20px" }}>
                  {`${selectedUsina?.name} - ${transformDateFormat(
                    relatorioDiario.dataDeReferencia
                  )}`}
                </Typography>
              </Grid>
              {reportErrors.map((error, i) => (
                <Grid key={i} sx={{ width: "100%" }} item xs={12}>
                  <Typography
                    sx={{
                      paddingBottom: "10px",
                      color: "#ff6060",
                      fontSize: 14,
                    }}
                  >
                    {error}
                  </Typography>
                </Grid>
              ))}
              <Grid sx={{ width: "100%" }} item xs={12}>
                <CardBox
                  dateLeft={`Dados do relatório coletados em: ${formatDateAndHour(
                    relatorioDiario.dataDeGeracao
                  )}`}
                  leftTopCalc={290}
                  widthTopCalc="305"
                  sx={{
                    height: "100%",
                    width: "100%",
                    paddingY: "32px",
                    display: "flex",
                    flexDirection: "column",
                    rowGap: "16px",
                  }}
                >
                  <Box
                    sx={{
                      flexDirection: "column",
                      display: "flex",
                      justifyContent: "space-around",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        flexDirection: { xs: "column", md: "row" },
                        display: "flex",
                        justifyContent: "space-around",
                      }}
                    >
                      <UsinaDetailsInfo
                        title="Código da Usina: "
                        value={relatorioDiario.dadosUsina?.cod ?? ""}
                      />
                      <UsinaDetailsInfo
                        title="Número de Inversores: "
                        value={
                          relatorioDiario.dadosUsina?.numeroInversores ?? 0
                        }
                      />
                    </Box>
                    <Box
                      sx={{
                        flexDirection: { xs: "column", md: "row" },
                        display: "flex",
                        justifyContent: "space-around",
                      }}
                    >
                      <UsinaDetailsInfo
                        title="Potência Nominal: "
                        value={`${numberPtBr(
                          relatorioDiario.dadosUsina?.potencia ?? 0 / 1000,
                          1
                        )} MW`}
                      />
                      <UsinaDetailsInfo
                        title="Número de Módulos: "
                        value={relatorioDiario.dadosUsina?.numeroModulos ?? 0}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexDirection: { xs: "column", md: "row" },
                      display: "flex",
                      width: "100%",
                      justifyContent: "space-around",
                    }}
                  >
                    <UsinaDetailsCard
                      title="PR Realizado"
                      value={prRealizado}
                    />
                    <UsinaDetailsCard
                      title="Irradiação Realizada"
                      value={irradiacaoMedia}
                    />
                    <UsinaDetailsCard
                      title="Potência Média Dia"
                      value={potenciaMedia}
                    />
                    {!disponibilidadeOnOffEnabled && (
                      <UsinaDetailsCard
                        title="Disponibilidade dos Inversores"
                        value={disponibilidadeMedia}
                      />
                    )}
                  </Box>
                  {disponibilidadeOnOffEnabled && (
                    <Box
                      sx={{
                        flexDirection: { xs: "column", md: "row" },
                        display: "flex",
                        width: "100%",
                        justifyContent: "space-around",
                      }}
                    >
                      <UsinaDetailsCard
                        title="Disponibilidade dos Inversores"
                        value={disponibilidadeMedia}
                      />
                      <UsinaDetailsCard
                        title="Disponibilidade do sistema"
                        value={disponibilidadeOnOff}
                      />
                      <UsinaDetailsCard
                        title="Disponibilidade contratual"
                        value={disponibilidadeTotal}
                      />
                    </Box>
                  )}
                </CardBox>
              </Grid>
              {relatorioDiario.acompanhamentoMensal && (
                <Grid sx={{ width: "100%" }} item xs={12}>
                  <CardBox
                    sx={{
                      height: "100%",
                      width: "100%",
                      minHeight: "400px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <PerformanceMonthlyChart acompanhamentoMensal={relatorioDiario.acompanhamentoMensal} />
                  </CardBox>
                </Grid>
              )}

              <Grid sx={{ width: "100%" }} item xs={12}>
                <Typography sx={{ paddingY: "20px", fontSize: "28px", fontWeight: "600", color: '#EE7D00' }}>
                  Performance da Usina
                </Typography>
              </Grid>

              {relatorioDiario?.compiladoUsina &&
                <>
                  <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                    <CardBox
                      sx={{
                        height: "100%",
                        width: "100%",
                        minHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <PerformanceRatioChart
                        prReal={relatorioDiario?.compiladoUsina.prReal}
                        prPrevisto={relatorioDiario?.compiladoUsina.prPrevisto}
                      />
                    </CardBox>
                  </Grid>
                  <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                    <CardBox
                      sx={{
                        height: "100%",
                        width: "100%",
                        minHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <GenerationComparisonChart
                        hasIdeal
                        energiaIdeal={energiaIdeal}
                        energiaPrevista={energiaPrevista}
                        energiaGerada={energiaGerada}
                        prReal={relatorioDiario?.compiladoUsina.prReal}
                        prPrevisto={relatorioDiario?.compiladoUsina.prPrevisto}
                      />
                    </CardBox>
                  </Grid>
                  <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                    <CardBox
                      sx={{
                        height: "100%",
                        width: "100%",
                        minHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <LossDiagramReportChart
                        perdas={relatorioDiario.compiladoUsina.perdas}
                      />
                    </CardBox>
                  </Grid>
                  <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                    <CardBox
                      sx={{
                        height: "100%",
                        width: "100%",
                        minHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <ExpectedAndGeneratedValueChart
                        valorEsperado={
                          relatorioDiario.compiladoUsina.valorEsperado
                        }
                        valorGerado={relatorioDiario.compiladoUsina.valorGerado}
                      />
                    </CardBox>
                  </Grid>
                </>
              }

              <Grid sx={{ width: "100%" }} item xs={12}>
                <Typography sx={{ paddingY: "20px", fontSize: "28px", fontWeight: "600", color: '#EE7D00' }}>
                  Analise de Desempenho Operacional
                </Typography>
              </Grid>

              {relatorioDiario.energiaGeradaInversoresMediaHoraria && (
                <GeneratedEnergyCharts
                  horas={
                    relatorioDiario.energiaGeradaInversoresMediaHoraria.horas
                  }
                  listaPerformanceUnidadeGeradora={
                    relatorioDiario.energiaGeradaInversoresMediaHoraria
                      .listaPerformanceUnidadeGeradora
                  }
                  listaEnergiaGeradaInversor={
                    relatorioDiario.energiaGeradaInversoresMediaHoraria
                      .listaEnergiaGeradaInversor
                  }
                  irradiacaoRealizadaDia={
                    relatorioDiario.irradiacaoRealizadaDia ?? undefined
                  }
                />
              )}

              {relatorioDiario.performanceInversores && (
                <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                  <CardBox
                    sx={{
                      height: "100%",
                      width: "100%",
                      minHeight: "400px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <InverterPerformanceCard
                      performanceInversores={
                        relatorioDiario.performanceInversores
                      }
                    />
                  </CardBox>
                </Grid>
              )}

              {relatorioDiario.energiaGeradaInversoresMediaHoraria && (
                <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                  <CardBox
                    sx={{
                      height: "100%",
                      width: "100%",
                      minHeight: "400px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <UnitPerformanceChart
                      horas={
                        relatorioDiario.energiaGeradaInversoresMediaHoraria
                          .horas
                      }
                      listaPerformanceUnidadeGeradora={
                        relatorioDiario.energiaGeradaInversoresMediaHoraria
                          .listaPerformanceUnidadeGeradora
                      }
                      listaEnergiaGeradaInversor={
                        relatorioDiario.energiaGeradaInversoresMediaHoraria
                          .listaEnergiaGeradaInversor
                      }
                    />
                  </CardBox>
                </Grid>
              )}

              {relatorioDiario.disponibilidadeDiaria && (
                <GeneratedAvailabilityCharts
                  disponibilidade={relatorioDiario.disponibilidadeDiaria}
                />
              )}

              <Grid sx={{ width: "100%" }} item xs={12}>
                <Typography sx={{ paddingY: "20px", fontSize: "28px", fontWeight: "600", color: '#EE7D00' }}>
                  Análise de falhas e ocorrências
                </Typography>
              </Grid>

              {relatorioDiario.paretoDeOcorrenciasNoDia && (
                <>
                  <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                    <CardBox
                      sx={{
                        height: "100%",
                        width: "100%",
                        minHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <ParetoReportChart
                        tipoFaixa="dia"
                        data={relatorioDiario.paretoDeOcorrenciasNoDia}
                      />
                    </CardBox>
                  </Grid>
                  <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                    <ParetoTable
                      data={relatorioDiario.paretoDeOcorrenciasNoDia}
                    />
                  </Grid>
                </>
              )}

              {relatorioDiario.ocorrenciaInversoresNoMes && (
                Object.entries(relatorioDiario.ocorrenciaInversoresNoMes).map(([ug, matriz]) => {
                  return <StringsHeatmap matriz={matriz} currentUg={ug} />
                })
              )}

              {relatorioDiario.trackerParetroDeOcorrenciasNoDia && (
                <>
                  <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                    <CardBox
                      sx={{
                        height: "100%",
                        width: "100%",
                        minHeight: "400px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <ParetoReportChart
                        tipoFaixa="dia"
                        title="Pareto de Tracker fora do alvo"
                        data={relatorioDiario.trackerParetroDeOcorrenciasNoDia}
                        timeDistribuition
                      />
                    </CardBox>
                  </Grid>
                </>
              )}

              {relatorioDiario.plantaMTTRMTBF && (
                relatorioDiario.plantaMTTRMTBF?.uGs.map((ug) => (
                  <>
                    <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                      <CardBox
                        sx={{
                          height: "100%",
                          width: "100%",
                          minHeight: "400px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <MTTRAndMTBFChart
                          data={ug.inversores}
                          isMTTR
                          ugName={ug.name}
                        />
                      </CardBox>
                    </Grid>
                    <Grid sx={{ width: "100%" }} item xs={12} md={6}>
                      <CardBox
                        sx={{
                          height: "100%",
                          width: "100%",
                          minHeight: "400px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <MTTRAndMTBFChart
                          data={ug.inversores}
                          isMTTR={false}
                          ugName={ug.name}
                        />
                      </CardBox>
                    </Grid>
                  </>
                ))
              )}
            </>
          )}
        </Grid>
      </Screen.Container>
      <ErrorModal
        contentText={errorModalData.message}
        open={showErrorModal}
        setOpen={closeErrorModal}
        title={errorModalData.title}
      />
    </Screen.Root>
  );
}

export function UsinaDetailsCard(props: {
  title: string;
  value: string | number;
}) {
  const { title, value } = props;
  return (
    <Box
      sx={{
        border: "1px solid #EE7D00",
        borderRadius: "16px",
        padding: "8px",
        minWidth: { xs: "240px", md: "150px", lg: "250px" },
        minHeight: "90px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        marginBottom: "8px",
      }}
    >
      <Typography
        sx={{ color: "#EE7D00", fontWeight: "600", fontSize: "19px" }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ color: "#EE7D00", fontWeight: "600", fontSize: "19px" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function UsinaDetailsInfo(props: {
  title: string;
  value: string | number;
}) {
  const { title, value } = props;
  return (
    <Box
      sx={{
        paddingY: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row",
      }}
    >
      <Typography sx={{ color: "#FFF", fontWeight: "600", fontSize: "16px" }}>
        {title}
      </Typography>
      <Typography
        sx={{
          color: "#FFF",
          fontSize: "14px",
          paddingTop: "3px",
          paddingLeft: "8px",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
